// Platform capability catalog — drives /platform (FeaturesPage) and mirrors
// /app/memory/PLATFORM_FEATURES.md. Icons are lucide-react names resolved in the page.

export const PLATFORM_HERO = {
  overline: "The Platform",
  title: "Everything WebsiteTech does, in one place",
  subtitle:
    "WebsiteTech.pro is a complete, AI-powered growth platform — multi-country websites, a 20-service lead-generation engine, intelligent audits, an AI Growth Advisor, automated redesigns, CRM and billing — all working together to turn visitors into customers.",
};

export const PLATFORM_STATS = [
  { value: "20+", label: "Lead-generation services" },
  { value: "7", label: "Pillars scored per website" },
  { value: "2", label: "Countries & sub-brands live" },
  { value: "100%", label: "AI-assisted workflows" },
];

export const PLATFORM_GROUPS = [
  {
    id: "ad-growth-studio",
    icon: "Sparkles",
    title: "AI Ad & Growth Studio",
    tagline: "One workflow: brand DNA → campaigns → ads → landing pages → leads.",
    link: { label: "Open the Studio overview", to: "/solutions/studio/ad-growth-studio" },
    features: [
      { title: "Business & Brand DNA", desc: "One versioned brand record every AI reads before it speaks.", badge: "New" },
      { title: "Page-to-Campaign", desc: "Any URL becomes a structured campaign brief in one paste.", badge: "New" },
      { title: "AI Copywriter", desc: "11 marketing frameworks × 9 tones × your Brand DNA.", badge: "New" },
      { title: "Static Ad Studio", desc: "Locked copy → on-brand image ads in every size (Gemini Nano Banana).", badge: "New" },
      { title: "Landing Page Generator", desc: "Every campaign ships with a matching page — not a hope.", badge: "New" },
      { title: "Approvals Inbox", desc: "Nothing ships without a human tap.", badge: "New" },
      { title: "AI Growth Manager", desc: "Weekly plain-language recommendations — advisory, never autopilot.", badge: "New" },
      { title: "Bulk Catalogue Import", desc: "A CSV of URLs → your whole catalogue as briefs by morning.", badge: "New" },
    ],
  },
  {
    id: "multi-country",
    icon: "Globe",
    title: "Multi-Country, Multi-Tenant Platform",
    tagline: "One codebase, independently deployable per country and sub-brand.",
    features: [
      { title: "India Portal (/in)", desc: "A dedicated India instance with INR pricing, local content and its own CRM — logically isolated by country." },
      { title: "Kerala.com Tenant (/kerala)", desc: "A full sub-brand with its own public site, CMS, CRM and City Partner ecosystem." },
      { title: "Malayalam Translation", desc: "On-demand regional translation across the Kerala experience." },
      { title: "Logical Data Isolation", desc: "Commercial data, pricing, leads and CRM separated by tenant and country." },
    ],
  },
  {
    id: "lead-gen",
    icon: "Target",
    title: "Lead Generation Ecosystem",
    tagline: "A 20-service acquisition engine with a structured mega-menu.",
    link: { label: "Explore services", to: "/lead-generation" },
    features: [
      { title: "20 Service Pages", desc: "SEO, AI search, paid ads, social, lead capture, qualification, instant response, appointments, CRM, nurturing, analytics, reputation and more." },
      { title: "Categorized Mega-Menu", desc: "Every service organized by growth stage — Attract, Capture, Convert, Retain." },
      { title: "Industry × Location Pages", desc: "Localized playbooks (challenges, funnel, KPIs) at clean SEO URLs — auto-generated by AI or hand-crafted.", badge: "New" },
    ],
  },
  {
    id: "advisor",
    icon: "Sparkles",
    title: "AI Growth Advisor",
    tagline: "A guided questionnaire that recommends a tailored growth plan.",
    link: { label: "Try the advisor", to: "/growth-advisor" },
    features: [
      { title: "Smart Questionnaire", desc: "A few questions about your business and goals produce a prioritized plan." },
      { title: "Recommended Bundle & Services", desc: "AI matches you to the right package and services with clear reasons." },
      { title: "Branded PDF Proposal", desc: "Get a professionally designed proposal emailed instantly.", badge: "New" },
      { title: "Automatic CRM Lead", desc: "Your enquiry drops into the right CRM, routed by location, for fast follow-up." },
    ],
  },
  {
    id: "audit",
    icon: "Gauge",
    title: "Website Intelligence & Audits",
    tagline: "Know exactly how well your site wins customers — and what to fix.",
    link: { label: "Run a free audit", to: "/audit" },
    features: [
      { title: "Lead Generation Readiness Score", desc: "A 0–100 score across 7 pillars: Visibility, Website, Conversion, Trust, Follow-Up, Automation and Analytics.", badge: "New" },
      { title: "Measured + AI-Estimated Signals", desc: "Four pillars measured from your live site; three AI-estimated and clearly labelled." },
      { title: "Prioritized Fixes", desc: "Ranked, specific actions tied directly to the services that solve them." },
      { title: "SEO + AI Visibility Engine", desc: "Page analysis, AEO/entity checks, competitor comparison, hreflang and cannibalization detection." },
      { title: "Instant Repeat Scans", desc: "Results are cached per domain so repeat audits return in seconds.", badge: "New" },
    ],
  },
  {
    id: "redesign",
    icon: "Wand2",
    title: "AI Website Redesign",
    tagline: "See a better version of your site before you commit.",
    features: [
      { title: "Before Screenshots", desc: "Live captures of your current site embedded into a polished deck." },
      { title: "AI Mockups (Nano Banana)", desc: "Gemini-powered 'after' concepts show a modern redesign direction." },
      { title: "Full Redesign PDF Decks", desc: "Shareable, branded decks generated automatically in the background." },
      { title: "Auto Outreach", desc: "Low-scoring prospects can receive a redesign preview automatically (admin-controlled)." },
    ],
  },
  {
    id: "pricing",
    icon: "Tag",
    title: "Service Bundles & Pricing",
    tagline: "Transparent, country-aware packages that scale with you.",
    link: { label: "See pricing", to: "/pricing" },
    features: [
      { title: "START · GROW · SCALE · PERFORMANCE", desc: "Four managed bundles from essentials to a full acquisition engine." },
      { title: "INR & USD", desc: "INR-first for India and Kerala, USD on the global site — with a live currency toggle.", badge: "New" },
      { title: "Admin-Editable", desc: "Prices and features are managed in the CMS and reflected instantly everywhere." },
    ],
  },
  {
    id: "crm",
    icon: "Users",
    title: "CRM & Lead Management",
    tagline: "Capture, route and convert every enquiry.",
    features: [
      { title: "Smart Lead Capture", desc: "Consultation, audit, advisor and contact forms feed a unified CRM." },
      { title: "Deterministic Routing & Dedup", desc: "Leads route to the right tenant and partner with atomic de-duplication." },
      { title: "1-Click Follow-Up", desc: "Convert enquiries into qualified CRM leads instantly." },
      { title: "Proposal Tracking", desc: "Every AI Growth Advisor proposal is tracked with its lead and a downloadable PDF.", badge: "New" },
    ],
  },
  {
    id: "ecosystem",
    icon: "Building2",
    title: "Partners, CMS & Content",
    tagline: "A managed content and partner network for local reach.",
    features: [
      { title: "City Partner Ecosystem", desc: "Kerala's partner network with onboarding, approval and auto-generated credentials by email." },
      { title: "Content Management", desc: "Manage public pages, galleries and localized content from the admin CMS." },
      { title: "Automated Emails (Resend)", desc: "Proposals, audit reports, partner credentials and redesign previews — all delivered automatically." },
    ],
  },
  {
    id: "admin",
    icon: "LayoutDashboard",
    title: "Admin Console & Automation",
    tagline: "Run the whole platform from one command center.",
    features: [
      { title: "Unified Admin", desc: "Audits, enquiries, analytics, clients, countries, redesign decks, pricing, industry pages and proposals." },
      { title: "AI Chat Assistants", desc: "On-site AI assistants help visitors and capture intent." },
      { title: "Billing", desc: "Razorpay for India and Stripe for global subscriptions and invoices." },
      { title: "Background Automation", desc: "Audits, PDFs, redesigns and emails run as tracked background jobs." },
    ],
  },
  {
    id: "growth-engine",
    icon: "Rocket",
    title: "Coming Soon — AI Growth Engine™",
    tagline: "Continuous, always-on website improvement by a team of AI agents.",
    comingSoon: true,
    features: [
      { title: "Continuous Growth Score", desc: "Your website is re-scored on a schedule with a clear trend over time." },
      { title: "AI Agent Team", desc: "Specialist agents diagnose issues and draft improvements across all 7 pillars." },
      { title: "Recommendations → Tasks → Safe Auto-Apply", desc: "Approve changes or let safe fixes apply automatically — always reversible." },
      { title: "GSC & GA4 Integrations", desc: "Real search and analytics data enrich every recommendation." },
    ],
  },
];
